import { Link } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { milestones, principles, siteInfo, stats, testimonials } from '@/mocks/site';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const DOMAIN = 'https://97go.cn';

export default function AboutPage() {
  useSEO({
    title: '关于我们 - 爆料前线的定位与内容原则 | 爆料前线',
    description:
      '了解爆料前线的品牌定位、发展理念与内容原则。我们坚持先核实再发布、以时间线组织进展，把今日鲜料与突发新闻整理成可回溯的阅读体验。',
    keywords: '关于爆料前线,内容原则,品牌介绍,爆料平台,实时热点',
    path: '/about',
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们',
        url: `${DOMAIN}/about`,
        inLanguage: 'zh-CN',
        description: '爆料前线的品牌定位、发展理念与内容原则。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '爆料前线',
        url: `${DOMAIN}/`,
        email: siteInfo.email,
        description: siteInfo.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${DOMAIN}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${DOMAIN}/about` },
        ],
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
          <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于爆料前线</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">{siteInfo.intro}</p>
          <dl className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {stats.map((item) => (
              <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 px-4 py-4 md:px-5">
                <dt className="text-xs text-foreground-500">{item.label}</dt>
                <dd className="mt-1.5 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {item.value}
                  <span className="ml-0.5 text-sm font-normal text-foreground-500">{item.suffix}</span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="border-b border-background-200 py-12 md:py-16" aria-labelledby="position-heading">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-800">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-compass-3-line text-sm" />
                </span>
                网站定位
              </span>
              <h2 id="position-heading" className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                把散落的热点，收拢成一条清晰的主线
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                热点事件往往是碎片的：一条热搜、一张截图、一段语音。观众真正费力的地方，不是获取信息，而是判断这件事现在走到了哪一步。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                爆料前线选择用时间线来组织内容。每一条话题都从最早的节点开始记录，后续出现补充或反转时直接追加，读者随时可以从头理清脉络，而不用在转发的信息里反复拼图。
              </p>
            </div>
            <div className="lg:col-span-6">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Stylized%20editorial%20illustration%20of%20a%20small%20newsroom%20desk%20with%20monitors%20showing%20rising%20trend%20charts%2C%20paper%20notes%20and%20coffee%20cups%2C%20warm%20cream%20background%20with%20bold%20orange%20and%20deep%20teal%20graphic%20shapes%2C%20soft%20grain%20texture%2C%20flat%20geometric%20poster%20style%2C%20calm%20minimal%20composition%2C%20no%20text&width=1200&height=800&seq=baoliao-about-01&orientation=landscape"
                  alt="编辑部工作台与热度图表的设计插图"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-100 py-12 md:py-16" aria-labelledby="principle-heading">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <h2 id="principle-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            内容原则
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这几条是编辑团队每天都在执行的标准，也是我们判断一条内容能不能发出去的底线。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-900">
                  <i className={`${item.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-background-200 py-12 md:py-16" aria-labelledby="milestone-heading">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <h2 id="milestone-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            发展理念
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            从一张内部表格到现在的信息结构，改变的是呈现方式，没变的是对「可回溯」的坚持。
          </p>
          <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {milestones.map((item, index) => (
              <li key={item.year} className="rounded-lg border border-background-200 bg-background-100 p-5">
                <span className="flex items-center gap-2">
                  <span
                    className={`flex h-7 w-7 items-center justify-center rounded-full font-heading text-xs font-bold ${
                      index % 2 === 0 ? 'bg-primary-500 text-background-50' : 'bg-accent-500 text-background-50'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="font-heading text-lg font-bold text-foreground-950">{item.year}</span>
                </span>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-100 py-12 md:py-16" aria-labelledby="voice-heading">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <h2 id="voice-heading" className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            用户怎么说
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {testimonials.map((item) => (
              <blockquote key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-6 w-6 items-center justify-center text-primary-400">
                  <i className="ri-double-quotes-l text-xl" />
                </span>
                <p className="mt-3 text-sm leading-relaxed text-foreground-800">{item.quote}</p>
                <footer className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 text-secondary-900">
                    <span className="font-heading text-sm font-bold">{item.name.slice(0, 1)}</span>
                  </span>
                  <span>
                    <cite className="block text-sm font-medium not-italic text-foreground-950">{item.name}</cite>
                    <span className="text-xs text-foreground-500">{item.role}</span>
                  </span>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-background-200 bg-secondary-100 px-6 py-8 md:flex-row md:items-center md:px-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">有料要爆？我们随时在</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                邮箱：{siteInfo.email} · 提供时间、地点和可核实的细节，编辑会优先处理。
              </p>
            </div>
            <Link
              to="/contact"
              className="flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-send-plane-line text-sm" />
              </span>
              前往联系我们
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}